import React, { useEffect, useState } from "react";
import api, { formatApiError, money, accountLabel, clientLabel } from "../lib/api";
import { SearchableSelect } from "./SearchableSelect";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const today = () => new Date(Date.now() - 216e5).toISOString().slice(0, 10);
const toInput = (d) => { if (!d) return today(); const p = String(d).split("/"); return p.length === 3 ? `${p[2]}-${p[1].padStart(2, "0")}-${p[0].padStart(2, "0")}` : d; };
const emptyForm = (cid = "") => ({ client_id: cid, method: "Efectivo", date: today(), reference: "", account_id: "", notes: "", commission: "", commission_note: "" });

export function ReceiptDialog({ open, onOpenChange, editing, presetClientId, presetClientName, presetDeliveries, onSaved }) {
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [clientInvoices, setClientInvoices] = useState([]);
  const [allocs, setAllocs] = useState({});
  const [saving, setSaving] = useState(false);
  const deliveryOnly = !!(presetDeliveries && presetDeliveries.length);

  useEffect(() => {
    Promise.all([api.get("/clients"), api.get("/finance/accounts").catch(() => ({ data: [] })), api.get("/finance/meta")])
      .then(([c, a, m]) => { setClients(c.data); setAccounts(a.data); setMethods(m.data.methods); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    if (editing) loadEdit(editing);
    else resetNew(presetClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, presetClientId]);

  const docsForClient = async (cid, allocByDoc = {}) => {
    const [invR, delR] = await Promise.all([
      api.get(`/clients/${cid}/open-invoices`, { params: { include_ids: Object.keys(allocByDoc).join(",") } }),
      api.get(`/clients/${cid}/open-deliveries`, { params: { include_ids: Object.keys(allocByDoc).join(",") } }).catch(() => ({ data: [] })),
    ]);
    const addBack = (bal, a) => Number((bal + (a.applied ?? (a.amount + (a.retention || 0)))).toFixed(2));
    const invs = invR.data
      .filter((i) => i.status !== "Anulada" && (i.balance > 0 || allocByDoc[i.id]))
      .map((i) => allocByDoc[i.id] ? { ...i, doc_type: "invoice", balance: addBack(i.balance, allocByDoc[i.id]) } : { ...i, doc_type: "invoice" });
    const dels = delR.data
      .filter((x) => x.balance > 0 || allocByDoc[x.id])
      .map((x) => allocByDoc[x.id] ? { ...x, doc_type: "delivery", balance: addBack(x.balance, allocByDoc[x.id]) } : { ...x, doc_type: "delivery" });
    return [...invs, ...dels];
  };

  const resetNew = async (cid) => {
    setForm(emptyForm(cid || ""));
    if (presetDeliveries && presetDeliveries.length) {
      const merged = presetDeliveries.map((x) => ({ ...x, doc_type: "delivery" }));
      setClientInvoices(merged);
      setAllocs(Object.fromEntries(merged.map((i) => [i.id, { amount: "", ret: "", doc: "" }])));
      return;
    }
    setClientInvoices([]); setAllocs({});
    if (cid) { const merged = await docsForClient(cid); setClientInvoices(merged); setAllocs(Object.fromEntries(merged.map((i) => [i.id, { amount: "", ret: "", doc: "" }]))); }
  };

  const loadEdit = async (row) => {
    try {
      const rc = (await api.get(`/receipts/${row.id}`)).data;
      const allocByDoc = Object.fromEntries((rc.allocations || []).map((a) => [a.invoice_id || a.delivery_id, a]));
      const merged = await docsForClient(rc.client_id, allocByDoc);
      setClientInvoices(merged);
      setAllocs(Object.fromEntries(merged.map((i) => { const a = allocByDoc[i.id]; return [i.id, { amount: a ? String(a.amount) : "", ret: a && a.retention ? String(a.retention) : "", doc: a?.retention_doc || "" }]; })));
      setForm({ client_id: rc.client_id, method: rc.method || "Efectivo", date: toInput(rc.date), reference: rc.reference || "", account_id: rc.account_id || "", notes: rc.notes || "", commission: rc.commission ? String(rc.commission) : "", commission_note: rc.commission_note || "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pickClient = async (cid) => {
    setForm((f) => ({ ...f, client_id: cid }));
    const merged = await docsForClient(cid);
    setClientInvoices(merged);
    setAllocs(Object.fromEntries(merged.map((i) => [i.id, { amount: "", ret: "", doc: "" }])));
  };

  const upd = (id, k, val) => setAllocs((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: val } }));
  const cashTotal = Object.values(allocs).reduce((s, v) => s + Number(v?.amount || 0), 0);
  const retTotal = Object.values(allocs).reduce((s, v) => s + Number(v?.ret || 0), 0);
  const appliedTotal = cashTotal + retTotal;
  const commission = Number(form.commission || 0);
  const netDeposit = cashTotal - commission;

  const save = async () => {
    const allocations = Object.entries(allocs)
      .filter(([, v]) => Number(v?.amount) > 0 || Number(v?.ret) > 0)
      .map(([id, v]) => {
        const item = clientInvoices.find((x) => x.id === id);
        const base = { amount: Number(v?.amount || 0), retention: Number(v?.ret || 0), retention_doc: (v?.doc || "").trim() };
        return item?.doc_type === "delivery" ? { delivery_id: id, amount: base.amount, retention: 0, retention_doc: "" } : { invoice_id: id, ...base };
      });
    if (!deliveryOnly && !form.client_id) return toast.error("Selecciona un cliente");
    if (allocations.length === 0) return toast.error("Ingresa al menos un abono o retención");
    if (allocations.find((a) => a.retention > 0 && !a.retention_doc)) return toast.error("Ingresa el número de constancia de la retención IVA");
    setSaving(true);
    try {
      const payload = { ...form, commission: Number(form.commission || 0), allocations };
      if (deliveryOnly) payload.client_name = presetClientName || "";
      if (editing) { const r = await api.put(`/receipts/${editing.id}`, payload); toast.success(`Recibo ${r.data.number} actualizado`); }
      else { const r = await api.post("/receipts", payload); toast.success(`Recibo ${r.data.number} generado`); }
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="receipt-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? `Editar recibo ${editing.number}` : "Nuevo recibo de caja"}</DialogTitle><DialogDescription>Cobro aplicado a facturas y envíos (venta sin factura) con saldo del cliente.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Cliente *</Label>
            {deliveryOnly
              ? <Input className="rounded-sm" disabled value={presetClientName || "Cliente sin registrar"} data-testid="receipt-client-fixed" />
              : <SearchableSelect value={form.client_id} onChange={pickClient} disabled={!!editing} testid="receipt-client"
                  placeholder="Selecciona" searchPlaceholder="Escribe para buscar cliente..."
                  options={clients.map((c) => ({ value: c.id, label: clientLabel(c) }))} />}</div>
          <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Forma de pago</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className="rounded-sm" data-testid="receipt-method"><SelectValue /></SelectTrigger>
              <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Fecha del cobro</Label>
            <Input type="date" className="rounded-sm" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="receipt-date" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Referencia</Label>
            <Input className="rounded-sm" placeholder="No. cheque / transf." value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Depositar en</Label>
            <Select value={form.account_id || "none"} onValueChange={(v) => setForm({ ...form, account_id: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-sm" data-testid="receipt-account"><SelectValue placeholder="Ninguna" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Sin depósito bancario</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{accountLabel(a)}</SelectItem>)}</SelectContent></Select></div>
        </div>

        {form.account_id && (
          <div className="space-y-2 border border-border rounded-sm p-3" data-testid="commission-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Comisión de tarjeta / Neonet (opcional)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Comisión</Label>
                <Input type="number" step="0.01" className="rounded-sm" value={form.commission} onChange={(e) => setForm({ ...form, commission: e.target.value })} placeholder="0.00" data-testid="receipt-commission" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Nota</Label>
                <Input className="rounded-sm" value={form.commission_note} onChange={(e) => setForm({ ...form, commission_note: e.target.value })} placeholder="Ej. Comisión Neonet 3%" data-testid="receipt-commission-note" /></div>
            </div>
            <p className="text-xs text-muted-foreground">La comisión se registra como egreso en la cuenta; al banco entra el <b>neto depositado</b> (efectivo − comisión). La retención de IVA no entra al banco.</p>
          </div>
        )}

        <div className="border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted text-left text-[10px] uppercase text-muted-foreground"><th className="px-2 py-2">Documento</th><th className="px-2 py-2">Fecha</th><th className="px-2 py-2 text-right">Saldo</th><th className="px-2 py-2 w-28 text-right">Abono</th><th className="px-2 py-2 w-28 text-right">Ret. IVA</th><th className="px-2 py-2 w-36">No. Constancia</th></tr></thead>
            <tbody>
              {clientInvoices.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground text-sm">{form.client_id ? "Este cliente no tiene facturas ni envíos con saldo." : "Selecciona un cliente."}</td></tr>}
              {clientInvoices.map((inv) => (
                <tr key={inv.id} className="border-t border-border" data-testid={`receipt-inv-${inv.number}`}>
                  <td className="px-2 py-1.5 font-mono text-xs font-bold text-primary whitespace-nowrap">{inv.number}{inv.doc_type === "delivery" && <Badge className="ml-1.5 rounded-sm bg-blue-100 text-blue-700 text-[9px] font-semibold align-middle">Envío</Badge>}</td>
                  <td className="px-2 py-1.5 text-xs">{inv.date}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{money(inv.currency, inv.balance)}</td>
                  <td className="px-2 py-1"><Input type="number" className="h-8 rounded-sm text-xs text-right" value={allocs[inv.id]?.amount ?? ""} onChange={(e) => upd(inv.id, "amount", e.target.value)} data-testid={`alloc-${inv.number}`} /></td>
                  <td className="px-2 py-1">{inv.doc_type === "delivery" ? <span className="block text-center text-xs text-muted-foreground">—</span> : <Input type="number" className="h-8 rounded-sm text-xs text-right" value={allocs[inv.id]?.ret ?? ""} onChange={(e) => upd(inv.id, "ret", e.target.value)} data-testid={`ret-${inv.number}`} />}</td>
                  <td className="px-2 py-1">{inv.doc_type === "delivery" ? <span className="block text-center text-xs text-muted-foreground">—</span> : <Input className="h-8 rounded-sm text-xs" placeholder="No. constancia" value={allocs[inv.id]?.doc ?? ""} onChange={(e) => upd(inv.id, "doc", e.target.value)} data-testid={`retdoc-${inv.number}`} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-end gap-4">
          <Textarea placeholder="Observaciones" className="rounded-sm flex-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="w-56 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Abono (efectivo)</span><span className="font-mono" data-testid="receipt-cash">{cashTotal.toFixed(2)}</span></div>
            {retTotal > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Retención IVA</span><span className="font-mono text-indigo-600" data-testid="receipt-retention">{retTotal.toFixed(2)}</span></div>}
            {commission > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Comisión</span><span className="font-mono text-rose-600" data-testid="receipt-commission-total">-{commission.toFixed(2)}</span></div>}
            {commission > 0 && form.account_id && <div className="flex justify-between"><span className="text-muted-foreground">Neto depositado</span><span className="font-mono font-semibold" data-testid="receipt-net">{netDeposit.toFixed(2)}</span></div>}
            <div className="flex justify-between border-t border-border pt-1 text-base font-bold text-primary"><span>APLICADO</span><span className="font-mono" data-testid="receipt-total">{appliedTotal.toFixed(2)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="rounded-sm font-bold" onClick={save} disabled={saving} data-testid="save-receipt-btn">{saving ? "Guardando..." : "Generar recibo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
